/**
 * AdminPage Component
 * Main page for administrative functions
 */

import React from 'react';
import { Container, Alert, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { AdminPanel, MessageErrorBoundary } from '../components/messaging';
import './AdminPage.css';

/**
 * AdminPage - Administrative interface
 * @returns {JSX.Element} AdminPage component
 */
const AdminPage = () => {
  const { user, isAdmin, loading, signOut, getDisplayName } = useAuth();
  const navigate = useNavigate();

  const handleBackToMessages = () => {
    navigate('/messages');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <Container className="admin-page">
        <div className="text-center py-5">
          <p className="text-muted">Loading...</p>
        </div>
      </Container>
    );
  }

  if (!isAdmin()) {
    return (
      <Container className="admin-page">
        <Alert variant="danger" className="mt-4">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>
            You do not have permission to access this page. Administrator privileges are required.
          </p>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="admin-page">
      {/* Admin page header with navigation */}
      <div className="admin-page-header">
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center p-3">
            <div>
              <h4 className="mb-0">Administration Panel</h4>
              <small className="text-muted">Welcome, {getDisplayName()}</small>
            </div>
            <div className="d-flex align-items-center gap-3">
              <Button 
                variant="outline-primary"
                size="sm"
                onClick={handleBackToMessages}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Messages
              </Button>
              
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={handleSignOut}
              >
                <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Admin panel content */}
      <MessageErrorBoundary 
        componentName="AdminPanel"
        onRetry={() => window.location.reload()}
      >
        <AdminPanel user={user} />
      </MessageErrorBoundary>
    </div>
  );
};

export default AdminPage;
